import type { Feature, FeatureCollection, Polygon } from 'geojson';
import { Mission, Zone } from '../db';
// `ZoneType` is a `const enum`; imported from the leaf module (not the `../db` barrel) because
// SEEDABLE_MAP_FEATURES below reads `ZoneType.PULLIN` at module-init time, and `../db` re-exports
// it through `MissionClass` -> `db_ops/mission_ops` -> this module, a cycle that would otherwise
// hand us an undefined `ZoneType` while `../db` is still mid-initialization (same reason
// `MissionClass.ts`'s own top-level `ZONE_TYPE_LABELS` map imports it from here too).
import { ZoneType } from '../db/ZoneTypesDatatype';
import { KML_FEATURE_TYPE, KmlFeatureType } from '../featureTypes';
import { processMultipolygonCoords } from '../services/BoundaryService';
import { MultiPolygonCoords } from '../types/types';

interface SeedableMapFeature {
  zoneName: KmlFeatureType;
  zoneType: ZoneType;
  /** The mission's existing zones of this type — when any exist, nothing is seeded. */
  existing: (mission: Mission) => Zone[];
}

/**
 * The kinds of `Jobs."Map Features GeoJSON (code)"` feature that mapmaking seeds into the mission, keyed
 * by the feature's `properties.name` — the portal writes MC's own KML feature names, so no
 * translation. v1: the dispatcher's pull-in zones → the generic (PULLIN) zone, the same one the
 * mapmaker's "Make Generic Zone" button creates and safe routing treats as a bridge. Adding
 * `unsafe`/`slow` is an entry here plus the matching entries in the portal's two vocabularies.
 */
export const SEEDABLE_MAP_FEATURES: Record<string, SeedableMapFeature> = {
  [KML_FEATURE_TYPE.PULLIN]: {
    zoneName: KML_FEATURE_TYPE.PULLIN,
    zoneType: ZoneType.PULLIN,
    existing: (mission) => mission.getPullinZones(),
  },
};

/** Parse the field's text, or `undefined` for anything that is not a FeatureCollection. Junk in
 *  a free-text Airtable field is data, never a reason for mapmaking to fail to open. */
export function parseMapFeatures(text: string | undefined): FeatureCollection | undefined {
  if (!text || !text.trim()) {
    return undefined;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch {
    return undefined;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return undefined;
  }

  const collection = parsed as Partial<FeatureCollection>;

  if (collection.type !== 'FeatureCollection' || !Array.isArray(collection.features)) {
    return undefined;
  }

  return collection as FeatureCollection;
}

/** A single-ring Polygon with a closed ring of ≥ 4 finite, in-range `[lng, lat]` positions. */
function isSeedablePolygon(feature: Feature): feature is Feature<Polygon> {
  const geometry = feature?.geometry;

  if (!geometry || geometry.type !== 'Polygon' || !Array.isArray(geometry.coordinates)) {
    return false;
  }

  if (geometry.coordinates.length !== 1) {
    return false;
  }

  const ring = geometry.coordinates[0];

  if (!Array.isArray(ring) || ring.length < 4) {
    return false;
  }

  for (const position of ring) {
    if (!Array.isArray(position) || position.length < 2) {
      return false;
    }

    const [lng, lat] = position;

    if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
      return false;
    }
  }

  const first = ring[0];
  const last = ring[ring.length - 1];

  return first[0] === last[0] && first[1] === last[1];
}

/** Drop a zone that ended up holding no boundary — every polygon rejected, or a throw part-way
 *  through seeding. Leaving it attached would read to `seedZonesFromMapFeatures`'s "already has a
 *  zone of this kind" guard as the mapmaker's own zone, blocking seeding for this mission forever. */
function disposeIfEmpty(zone: Zone | undefined): void {
  if (zone && zone.getOuterZoneBoundarys().length === 0) {
    zone.dispose();
  }
}

/**
 * Seed the mission's zones from the job's `Jobs."Map Features GeoJSON (code)"` — features a dispatcher
 * attached at scheduling time. Runs from `update_job_details_with_airtable`, i.e. on every
 * `update_mission_details` including each reopen of a saved KML, hence the guard: a kind is
 * seeded ONLY when the mission has no zone of that type yet, so a mapmaker's own (possibly
 * edited) generic zone is never duplicated. Each `pullin` Polygon becomes one poly of the single
 * generic zone, through the same loader path shapefile boundaries take
 * (`processMultipolygonCoords`: GeoJSON `[lng, lat]` in, MC `[lat, lon]` stored, ring length and
 * minimum area checked). Invalid text or features are skipped with a warning — never a throw.
 */
export async function seedZonesFromMapFeatures(
  mission: Mission,
  job: { mapFeaturesGeojsonCode?: string },
): Promise<void> {
  const collection = parseMapFeatures(job.mapFeaturesGeojsonCode);

  if (!collection) {
    return;
  }

  for (const [name, spec] of Object.entries(SEEDABLE_MAP_FEATURES)) {
    // The whole per-kind body is guarded: an exception anywhere in here (a bad geometry
    // tripping something in the geo path, say) must not escape and abort update_mission_details
    // for the rest of the job — the only callers up the chain surface failures via alertWarn +
    // Sentry. One bad map feature skips its own kind; it never blocks the rest of mapmaking.
    let zone: Zone | undefined;

    try {
      if (spec.existing(mission).length > 0) {
        continue;
      }

      const polygons: MultiPolygonCoords = [];

      for (const feature of collection.features) {
        if (!feature || feature.type !== 'Feature' || feature.properties?.name !== name) {
          continue;
        }

        if (!isSeedablePolygon(feature)) {
          console.warn(`Map features (${name}) for job ${mission.job_id}: skipping an invalid polygon`);
          continue;
        }

        polygons.push(feature.geometry.coordinates);
      }

      if (polygons.length === 0) {
        continue;
      }

      zone = Zone.create();
      zone.Mission_id = mission.instance_id;
      zone.zone_name = spec.zoneName;
      zone.zone_type = spec.zoneType;

      const errors = await processMultipolygonCoords(mission, polygons, zone);

      for (const error of errors) {
        console.warn(`Map features (${name}) for job ${mission.job_id}: ${error}`);
      }

      // Every polygon was rejected (too small, too few points).
      disposeIfEmpty(zone);
    } catch (error) {
      console.warn(
        `Map features (${name}) for job ${mission.job_id}: unexpected error while seeding — skipping`,
        error,
      );
      // The throw may have landed after Zone.create() and before any boundary attached, so the
      // warn alone is not enough: the half-built zone has to go too.
      disposeIfEmpty(zone);
    }
  }
}
